import React, { ChangeEvent } from 'react';

import { useUpdateEffect } from '@private/hooks';

import HintButton from 'components/HintButton';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { INullable } from 'utils/types';

import { CustomLabel, IInputProps, Label, LgInput, MdInput, SmInput } from './styled';

const Input: React.FC<IInputProps> = ({
  disablePasswordIcon,
  endIcon,
  hintIcon,
  hintText,
  inputRef,
  isAnimatedLabel = false,
  isAutoFocused,
  maxHintWidth,
  maxNumber,
  onChange,
  regExp,
  sizeVariant = 'lg',
  type,
  ...rest
}) => {
  const localRef = React.useRef<INullable<HTMLInputElement>>(null);
  const [currentType, setCurrentType] = React.useState(type);

  const currentRef = inputRef || localRef;

  useUpdateEffect(() => {
    if (isAutoFocused) {
      currentRef.current?.focus();
    }
  }, [isAutoFocused, currentRef.current]);

  useUpdateEffect(() => {
    setCurrentType(type);
  }, [type]);

  const icon = React.useMemo(() => {
    if (type === 'password' && !disablePasswordIcon) {
      if (currentType === 'password') {
        return <ICONS_MAP.Eye />;
      }
      return <ICONS_MAP.EyeClosed />;
    }

    return endIcon;
  }, [currentType, endIcon, type, disablePasswordIcon]);

  const handleTypeChange = React.useCallback(() => {
    setCurrentType((state) => (state === 'password' ? 'text' : 'password'));
  }, []);

  const onEndIconClick = React.useMemo(() => {
    if (type !== 'password') {
      return rest.onEndIconClick;
    }
    return handleTypeChange || rest.onIconClick;
  }, [handleTypeChange, rest.onEndIconClick, rest.onIconClick, type]);

  const endIconSize = React.useMemo(() => {
    if (type === 'password') {
      return 21;
    }
    return rest.endIconSize;
  }, [type, rest.endIconSize]);

  const onChangeHandler = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      if (regExp && value && !value.match(regExp)) {
        return;
      }
      const numberValue = Number(value);
      if (maxNumber && Number.isFinite(numberValue) && numberValue > maxNumber) {
        return;
      }
      onChange?.(e);
    },
    [onChange, regExp, maxNumber],
  );

  const onWheelHandler = React.useCallback(
    (e: React.WheelEvent<HTMLInputElement>) => {
      // A focused number input turns the wheel into a spinner; blurring lets the page scroll instead
      if (type === 'number') {
        e.currentTarget.blur();
      }
      rest.onWheel?.(e);
    },
    [type, rest.onWheel],
  );

  const Component = React.useMemo(() => {
    switch (sizeVariant) {
      case 'md':
        return MdInput;
      case 'sm':
        return SmInput;
      default:
        return LgInput;
    }
  }, [sizeVariant]);

  const renderCustomLabel = () => {
    if (!isAnimatedLabel && hintText) {
      return (
        <CustomLabel>
          <Label>{rest.label}</Label>
          <HintButton maxHintWidth={maxHintWidth} startIcon={hintIcon}>
            {hintText}
          </HintButton>
        </CustomLabel>
      );
    }
  };

  return (
    <Component
      {...rest}
      customLabel={renderCustomLabel()}
      endIcon={icon}
      endIconSize={endIconSize}
      inputRef={currentRef}
      isAnimatedLabel={isAnimatedLabel}
      onChange={onChangeHandler}
      onEndIconClick={onEndIconClick}
      onWheel={onWheelHandler}
      type={currentType}
    />
  );
};

export default appReactMemo(Input);
